import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { UseHashtagsReturn } from "./type";
import * as socialAPI from "../../Data/Social";
import { HashtagRecommendationType } from "../../Data/Social/types";

// Simplified hashtags state for trending names only
export interface HashtagsState {
    trending: string[]; // hashtag names (without #) for filter tabs
    recommendations: HashtagRecommendationType[]; // cached recommendations
    loading: boolean;
    error: string | null;
}

// Hashtags state atom
export const hashtagsState = atom<HashtagsState>({
    key: "hashtagsState" + randomString(4),
    default: {
        trending: [],
        recommendations: [],
        loading: false,
        error: null
    }
});

export const useHashtags = (): UseHashtagsReturn => {
    const [state, setState] = useRecoilState(hashtagsState);
    const resetHashtags = useResetRecoilState(hashtagsState);

    // Get trending hashtag names for filter tabs
    const getTrendingHashtags = useCallback(async (): Promise<string[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.getTrendingHashtagNames(5);
            
            if (result && Array.isArray(result)) {
                setState(prev => ({
                    ...prev,
                    trending: result,
                    loading: false
                }));
                
                return result;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get trending hashtags",
                loading: false
            }));
            console.error('Failed to get trending hashtags:', error);
            return [];
        }
    }, [setState]);

    // Get hashtag recommendations for content
    const getHashtagRecommendations = useCallback(async (query: string, limit: number = 10): Promise<HashtagRecommendationType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.getHashtagRecommendations(query, limit);
            
            if (result && Array.isArray(result)) {
                setState(prev => ({
                    ...prev,
                    recommendations: result,
                    loading: false
                }));
                
                return result;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get hashtag recommendations",
                loading: false
            }));
            console.error('Failed to get hashtag recommendations:', error);
            return [];
        }
    }, [setState]);

    return {
        hashtags: {}, // Empty for backward compatibility
        trending: state.trending,
        recommendations: state.recommendations,
        loading: state.loading,
        error: state.error,
        
        // Functions
        getTrendingHashtags,
        getHashtagRecommendations,

        reset: resetHashtags
    };
};